import React, { useState, useMemo } from 'react';
import { PropTypes } from 'prop-types';
import { get } from 'lodash';
import cx from 'classnames';
import pluralize from 'pluralize';
import { Field, Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import moment from 'moment';
import { feeSchedulePrograms as fspUtils } from '@unite-us/client-utils';
import { InputField, NoteDisclosure, validations } from '@unite-us/app-components';
import {
  DateField,
  RadioField,
  DurationField,
  TextField,
} from '@unite-us/ui';
import { useFeatureFlag } from 'common/hooks';
import { usePopulate, useFind } from 'api/APIHooks';
import { PENDING_AND_APPROVED_STATUSES } from 'pages/invoices/constants';
import { LineItem } from 'common/display/LineItem';
import { dollarAmount } from 'common/display/Money/DollarAmount';
import { validateUnitAmount } from 'common/form/Interactions/utils';
import DialogV2 from 'common/modal/DialogV2';
import datesInRange from 'common/utils/Dates/datesInRange';
import nearestDay from 'common/utils/Dates/nearestDay';
import formatDate from 'common/utils/Dates/formatDate';
import unixToISO from 'common/utils/Dates/unixToISO';
import dollarsToCents from 'common/utils/Money/dollarsToCents';
import ErrorMessage from 'common/utils/ErrorMessage';
import AuthorizationSpendSection from './AuthorizationSpendSection';
import { ClinicalModificationsDropdown } from './ClinicalModificationsDropdown';

const DATE_TYPE_VALUES = {
  SINGLE_DATE: 'single',
  RANGE_DATE: 'range',
};

const DATE_TYPE_OPTIONS = [
  { label: 'Single Date', value: DATE_TYPE_VALUES.SINGLE_DATE },
  { label: 'Date Range', value: DATE_TYPE_VALUES.RANGE_DATE },
];

const FORM_ERRORS = {
  AMOUNT_EXCEEDED: 'amount_exceeded',
  AUTHORIZED_AMOUNT_BELOW_INVOICED: 'authorized_amount_below_invoiced',
  DATES_OUT_OF_RANGE: 'dates_out_of_range',
  NOTE_REQUIRED: 'note_required',
};

const datesCoveredByInsurance = (formValues, authorization) => {
  const { insurance } = authorization;
  const startDate = moment.unix(formValues.range_date_start);
  const endDate = moment.unix(formValues.range_date_end);
  const singleDate = moment.unix(formValues.single_date);
  const enrolledAt = nearestDay(insurance.enrolled_at);
  const expiredAt = insurance.expired_at && nearestDay(insurance.expired_at);
  return formValues.date_type === DATE_TYPE_VALUES.RANGE_DATE ?
    datesInRange(enrolledAt, expiredAt, startDate, endDate) :
    datesInRange(enrolledAt, expiredAt, singleDate);
};

const isDateEntered = (values) => (
  (values.date_type === DATE_TYPE_VALUES.RANGE_DATE &&
    values.range_date_start && values.range_date_end) ||
  (values.date_type === DATE_TYPE_VALUES.SINGLE_DATE && values.single_date)
);

const ServiceAuthorizationFormModal = ({
  authorization,
  modalRef,
  title,
  confirmLabel,
  description,
  showRequestDetails,
  dataTestId,
  confirmButtonTestId,
  initialValues,
  onSubmit,
  isUpdateRequestMode,
}) => {
  const [isAmountExceeded, setIsAmountExceeded] = useState(false);
  const defaultQueryConfig = { queryConfig: { placeholderData: undefined } };
  usePopulate('insurance', 'insurance', authorization, defaultQueryConfig);
  usePopulate('case', 'case', authorization, defaultQueryConfig);
  usePopulate('case.person', 'person', authorization, defaultQueryConfig);
  const feeScheduleProgram = get(authorization, 'fee_schedule_program', {});
  const person = get(authorization, 'case.person', {});
  const updateEditAuthorizationsFeatureEnabled = useFeatureFlag('pays-5650-update-edit-authorizations');

  const {
    data: invoicedSpendData,
    isSuccess: isInvoicedSpendLoaded,
  } = useFind(
    'invoiced_spend',
    {
      fee_schedule_program: feeScheduleProgram.id,
      person: person.id,
      service_authorization: authorization.id,
      state: PENDING_AND_APPROVED_STATUSES.join(','),
    },
    {
      queryConfig: {
        enabled: Boolean(
          feeScheduleProgram.id && person.id && authorization.id &&
          updateEditAuthorizationsFeatureEnabled,
        ),
        refetchOnMount: 'always',
        placeholderData: undefined,
      },
    },
  );

  const totalInvoicedSpend = get(invoicedSpendData, 'data.meta.total_spend', 0);

  const initialClinicalModificationCodeValue = useMemo(() => {
    if (authorization.clinical_modifications?.length > 0) {
      return {
        value: authorization.clinical_modifications[0].code,
        label: authorization.clinical_modifications[0].code,
        id: authorization.clinical_modifications[0].id,
      };
    }
    return null;
  }, [authorization.clinical_modifications]);

  const formInitialValues = useMemo(() => ({
    ...initialValues,
    clinical_modification_code: initialClinicalModificationCodeValue,
  }), [initialValues, initialClinicalModificationCodeValue]);

  const closeModal = ({ reset }) => {
    if (modalRef.current) {
      reset();
      modalRef.current.closeDialog();
    }
  };

  const isFspLoaded = !!feeScheduleProgram.id;
  const isCostBased = fspUtils.isCostPaymentType(feeScheduleProgram.payment_type);
  const isUnitBased = !isCostBased;
  const isSingleDateRequested = authorization.requested_starts_at === authorization.requested_ends_at;
  const isSingleDateApproved = authorization.approved_starts_at === authorization.approved_ends_at;

  const handleSubmit = async (formData, form) => {
    const {
      payer_authorization_number,
      authorized_amount,
      date_type,
      single_date,
      range_date_start,
      range_date_end,
      reviewer_note,
      update_request_note,
      clinical_modification_code,
    } = formData;

    await onSubmit({
      payer_authorization_number,
      reviewer_note,
      update_request_note,
      ...(isUpdateRequestMode ? {
        ...(isUnitBased ?
          { requested_unit_amount: parseInt(authorized_amount, 10) } :
          { requested_cents: dollarsToCents(authorized_amount) }
        ),
        requested_starts_at: unixToISO(
          date_type === DATE_TYPE_VALUES.SINGLE_DATE ?
            single_date :
            range_date_start,
        ),
        requested_ends_at: unixToISO(
          date_type === DATE_TYPE_VALUES.SINGLE_DATE ?
            single_date :
            range_date_end,
        ),
      } : {
        ...(isUnitBased ?
          { approved_unit_amount: parseInt(authorized_amount, 10) } :
          { approved_cents: dollarsToCents(authorized_amount) }
        ),
        approved_starts_at: unixToISO(
          date_type === DATE_TYPE_VALUES.SINGLE_DATE ?
            single_date :
            range_date_start,
        ),
        approved_ends_at: unixToISO(
          date_type === DATE_TYPE_VALUES.SINGLE_DATE ?
            single_date :
            range_date_end,
        ),
      }),
      clinical_modifications: [{
        id: clinical_modification_code?.id,
        type: 'clinical_modification',
      }],
    });
    closeModal(form);
  };

  return (
    <Form
      keepDirtyOnReinitialize
      initialValues={formInitialValues}
      onSubmit={handleSubmit}
      validate={(values) => {
        const areDatesSelected = values.date_type === DATE_TYPE_VALUES.SINGLE_DATE ?
          !!values.single_date :
          !!values.range_date_start && values.range_date_end;

        if (isAmountExceeded && isDateEntered(values)) {
          return { [FORM_ERROR]: FORM_ERRORS.AMOUNT_EXCEEDED };
        }

        if (values.authorized_amount && isInvoicedSpendLoaded) {
          const parsedAuthorizedAmount = isUnitBased ?
            parseInt(values.authorized_amount, 10) :
            dollarsToCents(values.authorized_amount);

          if (parsedAuthorizedAmount < totalInvoicedSpend) {
            return { [FORM_ERROR]: FORM_ERRORS.AUTHORIZED_AMOUNT_BELOW_INVOICED };
          }
        }

        if (areDatesSelected && !datesCoveredByInsurance(values, authorization)) {
          return { [FORM_ERROR]: FORM_ERRORS.DATES_OUT_OF_RANGE };
        }

        if (isUpdateRequestMode && !values.update_request_note) {
          return { [FORM_ERROR]: FORM_ERRORS.NOTE_REQUIRED };
        }

        return {};
      }}
      render={({
        error,
        form,
        handleSubmit: formHandleSubmit,
        valid,
        values: {
          authorized_amount,
          date_type,
          range_date_start,
          range_date_end,
          single_date,
        },
      }) => (
        <DialogV2
          ref={modalRef}
          title={title}
          confirmLabel={confirmLabel}
          cancelHandler={() => closeModal(form)}
          confirmationHandler={formHandleSubmit}
          confirmationBtnDisabled={!valid}
          dataTestId={dataTestId}
          confirmButtonTestId={confirmButtonTestId}
          width="3xl"
        >
          <p>{description}</p>
          {isUpdateRequestMode && (
            <div className="pt-6">
              <div className="grid grid-cols-2 gap-2 m-0 gap-x-24">
                {(authorization.approved_cents || authorization.approved_unit_amount) && isFspLoaded && (
                  <div>
                    <label className="block mb-4" htmlFor="authorized-amount">Authorized Amount</label>
                    <p data-test-element="authorized-amount">
                      {isUnitBased ?
                        `${authorization.approved_unit_amount} ${pluralize(
                          feeScheduleProgram.unit || 'unit',
                          authorization.approved_unit_amount,
                        )}` :
                        dollarAmount(authorization.approved_cents, true)}
                    </p>
                  </div>
                )}
                {(authorization.approved_starts_at || authorization.approved_ends_at) && (
                  <div>
                    <label className="block mb-4" htmlFor="authorized-delivery-dates">
                      {isSingleDateApproved ?
                        'Authorized Service Delivery Date' :
                        'Authorized Service Delivery Dates'}
                    </label>
                    <p data-test-element="authorized-delivery-dates">
                      {isSingleDateApproved ?
                        formatDate(authorization.approved_ends_at || authorization.approved_starts_at) :
                        `${formatDate(authorization.approved_starts_at)} - ${
                          formatDate(authorization.approved_ends_at)
                        }`}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          {showRequestDetails && (
            <div className="pt-6">
              <h4 className="leading-4 mb-3 uppercase">
                Request Details
              </h4>
              <dl className="grid grid-cols-2 gap-2 m-0">
                <LineItem field="Amount Requested" dataTestElement="amount-requested">
                  {authorization.requested_unit_amount ?
                    authorization.requested_unit_amount :
                    dollarAmount(authorization.requested_cents, true)}
                </LineItem>
                {isSingleDateRequested ? (
                  <LineItem field="Service Delivery Date" dataTestElement="service-delivery-date">
                    {formatDate(authorization.requested_ends_at)}
                  </LineItem>
                ) : (
                  <LineItem field="Service Delivery Dates" dataTestElement="service-delivery-dates">
                    {formatDate(authorization.requested_starts_at)} -{' '}
                    {formatDate(authorization.requested_ends_at)}
                  </LineItem>
                )}
                <LineItem field="Payment Approach" dataTestElement="payment-approach">
                  {fspUtils.PAYMENT_TYPES[feeScheduleProgram.payment_type]}
                </LineItem>
                {isUnitBased && (
                  <LineItem field="Rate" dataTestElement="request-rate">
                    {dollarAmount(feeScheduleProgram.unit_rate, true)} per{' '}
                    {feeScheduleProgram.unit}
                  </LineItem>
                )}
                <LineItem field="Program Cap" dataTestElement="request-cap">
                  {/* eslint-disable-next-line react/no-danger */}
                  <div dangerouslySetInnerHTML={{ __html: feeScheduleProgram.cap_information || 'N/A' }} />
                </LineItem>
              </dl>
            </div>
          )}
          {!isUpdateRequestMode && (
            <AuthorizationSpendSection
              feeScheduleProgram={feeScheduleProgram}
              person={authorization?.case?.person}
              isUnitBased={isUnitBased}
              requestedStartsAt={
                moment.unix(
                  date_type === DATE_TYPE_VALUES.RANGE_DATE ?
                    range_date_start :
                    single_date,
                )
              }
              requestedEndsAt={
                moment.unix(
                  date_type === DATE_TYPE_VALUES.RANGE_DATE ?
                    (range_date_end || range_date_start) :
                    single_date,
                )
              }
              authorizedAmount={authorized_amount}
              setIsAmountExceeded={setIsAmountExceeded}
              excludeAuthorizationId={authorization?.original_service_authorization?.id || authorization?.id}
            />
          )}
          <hr className="my-5 border-dark-fill-blue border-solid" />
          {!isUpdateRequestMode && (
            <h4 className="uppercase leading-4 mb-3">
              Authorization Details
            </h4>
          )}
          <div className="grid grid-cols-2 gap-2 m-0 gap-x-24">
            <Field
              name="date_type"
              type="select"
            >
              {(props) => (
                <RadioField
                  className="mb-0"
                  label="PERIOD OF SERVICE"
                  options={DATE_TYPE_OPTIONS}
                  {...props}
                  required
                />
              )}
            </Field>
            {date_type === DATE_TYPE_VALUES.SINGLE_DATE && (
              <Field
                name="single_date"
                validate={(value) => validations.isRequired(value)}
              >
                {(fieldProps) => (
                  <DateField
                    {...fieldProps}
                    className="mb-0"
                    id="single_date"
                    label="SERVICE DELIVERY DATE"
                    required
                  />
                )}
              </Field>
            )}
            {date_type === DATE_TYPE_VALUES.RANGE_DATE && (
              <Field name="range_date_start" validate={(value) => validations.isRequired(value)}>
                {(startProps) => (
                  <Field name="range_date_end" validate={(value) => validations.isRequired(value)}>
                    {(endProps) => (
                      <DurationField
                        className="mb-0"
                        style={{ dropdown: { right: 0, left: 'unset' } }}
                        id="duration_field"
                        isUTC
                        label="SERVICE DELIVERY DATES"
                        startField={startProps}
                        endField={endProps}
                        required
                      />
                    )}
                  </Field>
                )}
              </Field>
            )}
            {error === FORM_ERRORS.DATES_OUT_OF_RANGE && (
              <div className="flex col-span-2 space-x-2 px-0 mt-0 pb-4">
                <ErrorMessage>
                  The authorized service delivery dates fall outside the
                  client&apos;s enrollment dates for the associated social
                  care coverage plan. Please change the authorized service
                  delivery dates.
                </ErrorMessage>
              </div>
            )}
            {isUnitBased ? (
              <Field name="authorized_amount" validate={validateUnitAmount}>
                {(fieldProps) => (
                  <InputField
                    {...fieldProps}
                    className={cx('mb-0', (
                      error === FORM_ERRORS.AMOUNT_EXCEEDED ||
                      error === FORM_ERRORS.AUTHORIZED_AMOUNT_BELOW_INVOICED
                    ) && 'ui-input-field--has-error')}
                    id="authorized_amount"
                    label="AUTHORIZED AMOUNT"
                    required
                    type="number"
                  />
                )}
              </Field>
            ) : (
              <Field
                name="authorized_amount"
                validate={
                  (value) => (
                    validations.isRequired(value) ||
                    validations.isGreaterThan(value, null, 0) ||
                    validations.isLessThan(value, 'Should be less than $1,000,000', 1_000_000)
                  )
                }
              >
                {(fieldProps) => (
                  <InputField
                    {...fieldProps}
                    className={cx('mb-0', (
                      error === FORM_ERRORS.AMOUNT_EXCEEDED ||
                      error === FORM_ERRORS.AUTHORIZED_AMOUNT_BELOW_INVOICED
                    ) && 'ui-input-field--has-error')}
                    id="authorized_amount"
                    label="AUTHORIZED AMOUNT"
                    placeholder="0.00"
                    required
                    step="0.1"
                    type="number"
                  />
                )}
              </Field>
            )}
            <div>
              <label className="block mb-4" htmlFor="service-authorization-unit-info">Unit</label>
              <p className="capitalize" id="service-authorization-unit-info">
                {isCostBased ? 'Dollars' : feeScheduleProgram.unit}
              </p>
            </div>

            {error === FORM_ERRORS.AMOUNT_EXCEEDED && (
              <div className="flex col-span-2 space-x-2 px-0 mt-0 pb-4">
                <ErrorMessage>
                  The authorized amount is more than the available amount.
                  Please adjust the authorized amount.
                </ErrorMessage>
              </div>
            )}

            {error === FORM_ERRORS.AUTHORIZED_AMOUNT_BELOW_INVOICED && (
              <div className="flex col-span-2 space-x-2 px-0 mt-0 pb-4">
                <ErrorMessage>
                  The amount requested cannot be less than what has already been invoiced.
                  Please adjust the amount requested.
                </ErrorMessage>
              </div>
            )}

            {!isUpdateRequestMode && (
              <Field name="payer_authorization_number">
                {(fieldProps) => (
                  <InputField
                    {...fieldProps}
                    className="mb-0"
                    id="payer_authorization_number"
                    label="PAYER AUTHORIZATION ID"
                  />
                )}
              </Field>
            )}
            {
              feeScheduleProgram.cm_code_required_on_authorization && !isUpdateRequestMode && (
                <ClinicalModificationsDropdown
                  initialValue={initialClinicalModificationCodeValue}
                />
              )
            }

            <Field name={isUpdateRequestMode ? 'update_request_note' : 'reviewer_note'}>
              {(props) => (
                <TextField
                  {...props}
                  className="p-0 col-span-2"
                  id={isUpdateRequestMode ? 'update_request_note' : 'reviewer_note'}
                  label="NOTE"
                  afterLabelContent={<NoteDisclosure />}
                  required={isUpdateRequestMode}
                />
              )}
            </Field>
          </div>
        </DialogV2>
      )}
    />
  );
};

ServiceAuthorizationFormModal.defaultProps = {
  showRequestDetails: false,
  description: '',
  isUpdateRequestMode: false,
};

ServiceAuthorizationFormModal.propTypes = {
  authorization: PropTypes.shape({
    id: PropTypes.string.isRequired,
    original_service_authorization: PropTypes.string,
    fee_schedule_program: PropTypes.shape({
      fee_schedule: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    }).isRequired,
    requested_cents: PropTypes.number,
    requested_starts_at: PropTypes.string.isRequired,
    requested_ends_at: PropTypes.string.isRequired,
    requested_unit_amount: PropTypes.number,
    approved_cents: PropTypes.number,
    approved_starts_at: PropTypes.string,
    approved_ends_at: PropTypes.string,
    approved_unit_amount: PropTypes.number,
    short_id: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    case: PropTypes.shape({
      person: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    }),
    clinical_modifications: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        code: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  modalRef: PropTypes.shape({
    current: PropTypes.any,
  }).isRequired,
  title: PropTypes.string.isRequired,
  confirmLabel: PropTypes.string.isRequired,
  description: PropTypes.node,
  showRequestDetails: PropTypes.bool,
  dataTestId: PropTypes.string.isRequired,
  confirmButtonTestId: PropTypes.string.isRequired,
  initialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isUpdateRequestMode: PropTypes.bool,
};

export default ServiceAuthorizationFormModal;
